import {
  NodoDto,
  RegistroDto,
  UpdateRegistroDto,
  useGetRegistriDispositivo,
} from "@/api";
import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  Card,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { isNil, keyBy } from "lodash";
import { Control, Controller } from "react-hook-form";
import { RicetteCard_GFL_TFLD_550_2 } from "./GFL_TFLD_550_2_RicetteCard";
import {
  formRicetteToRegistri,
  registriRicetteToForm,
} from "./GFL_TFLD_550_2_ricette";

type NumberFieldConfig = {
  name: string;
  label: string;
  decimals?: number;
  min?: number;
  max?: number;
  unit?: string;
};

type BoolFieldConfig = {
  name: string;
  label: string;
};

type TextFieldConfig = {
  name: string;
  label: string;
};

// TODO: Il file di protocollo (gatti macchina eaton.xlsx) mappa "durata lavoro
// manuale" sullo stesso registro di "durata pulizia iniziale". In attesa che ZM
// comunichi il nome corretto usiamo questo segnaposto.
const REGISTRO_DURATA_LAVORO_MANUALE = "ASPETTARE_RISPOSTA_ZM";

const numericFields: NumberFieldConfig[] = [
  {
    name: "tpulstart",
    label: "Durata pulizia iniziale",
    decimals: 1,
    min: 0,
  },
  {
    name: REGISTRO_DURATA_LAVORO_MANUALE,
    label: "Durata lavoro manuale",
    decimals: 1,
    min: 0,
  },
  {
    name: "satragg",
    label: "Saturazione da raggiungere",
    decimals: 1,
    min: 0,
    max: 100,
    unit: "%",
  },
  {
    name: "tstoprh1",
    label: "Temperatura stop resistenza RH1",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "tstoprh2",
    label: "Temperatura stop resistenza RH2",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "tmaxrh1",
    label: "Temperatura massima resistenza RH1",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "tmaxrh2",
    label: "Temperatura massima resistenza RH2",
    decimals: 1,
    unit: "°C",
  },
  {
    name: "presps1max",
    label: "Pressione massima PS1 (intasamento filtro)",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "prespt1max",
    label: "Pressione massima PT1 (vuoto troppo alto)",
    decimals: 1,
    unit: "bar",
  },
  {
    name: "tstopbatt",
    label: "Tempo spegnimento batteria",
    decimals: 1,
    min: 0,
  },
];

const boolFields: BoolFieldConfig[] = [
  { name: "funzman", label: "Selettore manuale/automatico" },
  { name: "abpulstart", label: "Abilitazione pulizia iniziale" },
  { name: "bypassbatt", label: "Bypass batteria" },
];

const textFields: TextFieldConfig[] = [
  { name: "email1", label: "Email 1" },
  { name: "email2", label: "Email 2" },
];

const supportedFields = [
  ...numericFields.map((x) => x.name),
  ...boolFields.map((x) => x.name),
  ...textFields.map((x) => x.name),
] as const;

type RegistroFieldName = (typeof supportedFields)[number];
type Registri_GFL_TFLD_550_2 = Record<RegistroFieldName, any>;

const numericFieldMap = Object.fromEntries(
  numericFields.map((field) => [field.name, field])
) as Record<string, NumberFieldConfig>;

const boolFieldMap = Object.fromEntries(
  boolFields.map((field) => [field.name, field])
) as Record<string, BoolFieldConfig>;

const textFieldMap = Object.fromEntries(
  textFields.map((field) => [field.name, field])
) as Record<string, TextFieldConfig>;

function parseNumber(value: string | undefined) {
  if (isNil(value) || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseBool(value: string | undefined) {
  if (isNil(value)) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  return ["1", "true", "on"].includes(normalized);
}

function mapEmptyStringToSlash(value: string) {
  return value === "" ? "/" : value;
}

export function registriToForm_GFL_TFLD_550_2(
  _registri: RegistroDto[]
): Partial<Registri_GFL_TFLD_550_2> & Record<string, any> {
  const registri = keyBy(_registri, "registro");
  const result: Partial<Registri_GFL_TFLD_550_2> & Record<string, any> = {};

  for (const field of supportedFields) {
    const raw = registri[field]?.value;

    if (numericFieldMap[field]) {
      result[field] = parseNumber(raw);
      continue;
    }

    if (boolFieldMap[field]) {
      result[field] = parseBool(raw);
      continue;
    }

    result[field] = raw === "/" ? "" : raw || "";
  }

  // Le ricette hanno chiavi dinamiche (ricetta_<indice>_<parametro>): si
  // ricavano dai registri ricevuti, non da un elenco statico.
  Object.assign(result, registriRicetteToForm(_registri));

  return result;
}

export function formToRegistri_GFL_TFLD_550_2(
  form: Partial<Registri_GFL_TFLD_550_2> & Record<string, any>
): UpdateRegistroDto[] {
  const topicPrefix = "var";

  const statici = supportedFields
    .map((field) => {
      const value = form[field];
      if (isNil(value)) {
        return undefined;
      }

      if (numericFieldMap[field]) {
        return { registro: field, topicPrefix, value };
      }

      if (boolFieldMap[field]) {
        return { registro: field, topicPrefix, value: value ? "1" : "0" };
      }

      return {
        registro: field,
        topicPrefix,
        value: mapEmptyStringToSlash(String(value)),
      };
    })
    .filter(Boolean) as UpdateRegistroDto[];

  return [...statici, ...formRicetteToRegistri(form, topicPrefix)];
}

const mappedFields: RegistroFieldName[] = [
  "funzman",
  "abpulstart",
  "tpulstart",
  REGISTRO_DURATA_LAVORO_MANUALE,
  "satragg",
  "tstoprh1",
  "tstoprh2",
  "tmaxrh1",
  "tmaxrh2",
  "presps1max",
  "prespt1max",
  "bypassbatt",
  "tstopbatt",
  "email1",
  "email2",
];

const otherFields = supportedFields.filter(
  (field) => !mappedFields.includes(field)
) as RegistroFieldName[];

export function EditorRegistri_GFL_TFLD_550_2({
  nodo,
  control,
  readOnly = false,
}: {
  nodo: NodoDto;
  control: Control<Registri_GFL_TFLD_550_2>;
  readOnly?: boolean;
}) {
  const { data: registri } = useGetRegistriDispositivo(nodo.id);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Ciclo">
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.funzman}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.abpulstart}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tpulstart"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName={REGISTRO_DURATA_LAVORO_MANUALE}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="satragg"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Soglie temperatura">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tstoprh1"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tstoprh2"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tmaxrh1"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tmaxrh2"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Soglie pressione">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="presps1max"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="prespt1max"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="Batteria">
            <Grid item xs={12}>
              <BooleanField
                control={control}
                config={boolFieldMap.bypassbatt}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tstopbatt"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <RicetteCard_GFL_TFLD_550_2
            registri={registri?.data}
            control={control}
            readOnly={readOnly}
          />
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Email">
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email1"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email2"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        {otherFields.length > 0 && (
          <Grid item xs={12}>
            <SectionCard title="Altro">
              {otherFields.map((fieldName) => {
                if (numericFieldMap[fieldName]) {
                  return (
                    <Grid item xs={12} md={6} key={fieldName}>
                      <NumberRegistroCell
                        control={control}
                        fieldName={fieldName}
                        readOnly={readOnly}
                      />
                    </Grid>
                  );
                }

                if (boolFieldMap[fieldName]) {
                  return (
                    <Grid item xs={12} md={6} key={fieldName}>
                      <BooleanField
                        control={control}
                        config={boolFieldMap[fieldName]}
                        readOnly={readOnly}
                      />
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} md={6} key={fieldName}>
                    <TextRegistroCell
                      control={control}
                      fieldName={fieldName}
                      readOnly={readOnly}
                    />
                  </Grid>
                );
              })}
            </SectionCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function NumberField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_GFL_TFLD_550_2>;
  config: NumberFieldConfig;
  readOnly: boolean;
}) {
  return (
    <Controller
      control={control}
      name={config.name as any}
      rules={{
        validate: (value) => {
          if (isNil(value) || value === "") {
            return true;
          }

          const numeric = Number(value);
          if (!Number.isFinite(numeric)) {
            return "Inserisci un numero valido";
          }

          if (!isNil(config.min) && numeric < config.min) {
            return `Il valore deve essere maggiore o uguale a ${config.min}`;
          }

          if (!isNil(config.max) && numeric > config.max) {
            return `Il valore deve essere minore o uguale a ${config.max}`;
          }

          return true;
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <Stack
          direction="column"
          alignItems="end"
          sx={{ width: "100%", maxWidth: 200 }}
        >
          <NumberInput
            {...field}
            numericInputProps={{ decimalScale: config.decimals ?? 0 }}
            endAdornment={
              config.unit ? (
                <InputAdornment position="end">{config.unit}</InputAdornment>
              ) : undefined
            }
            disabled={readOnly}
          />
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Stack>
      )}
    />
  );
}

function NumberRegistroCell({
  control,
  fieldName,
  readOnly,
}: {
  control: Control<Registri_GFL_TFLD_550_2>;
  fieldName: RegistroFieldName;
  readOnly: boolean;
}) {
  const config = numericFieldMap[fieldName];
  if (!config) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="row" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text" flex={1}>
        {config.label}
      </Typography>
      <NumberField control={control} config={config} readOnly={readOnly} />
    </Box>
  );
}

function BooleanField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_GFL_TFLD_550_2>;
  config: BoolFieldConfig;
  readOnly: boolean;
}) {
  return (
    <Controller
      name={config.name as any}
      control={control}
      render={({ field }) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{ minWidth: 220 }}
          >
            {config.label}
          </Typography>
          <Switch
            checked={field.value || false}
            onChange={(event) => field.onChange(event.target.checked)}
            disabled={readOnly}
          />
        </Stack>
      )}
    />
  );
}

function TextRegistroCell({
  control,
  fieldName,
  readOnly,
}: {
  control: Control<Registri_GFL_TFLD_550_2>;
  fieldName: RegistroFieldName;
  readOnly: boolean;
}) {
  const config = textFieldMap[fieldName];
  if (!config) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text">
        {config.label}
      </Typography>
      <Controller
        control={control}
        name={config.name as any}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="end" sx={{ width: "100%" }}>
            <TextField
              {...field}
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!error}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </Stack>
        )}
      />
    </Box>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <Box p={2}>
        <Typography variant="h5" color="text" fontWeight="medium" gutterBottom>
          {title}
        </Typography>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Box>
    </Card>
  );
}
